import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { FileText, ArrowRight, ExternalLink, Newspaper, MapPin, Calendar } from 'lucide-react';

// Featured External News Headlines
const EXTERNAL_NEWS = [
  {
    id: 1,
    title: "Embaixadora de Angola em Espanha apresenta cartas Figuradas a Ministra dos Negocios Estrangeiros de Andorra",
    source: "A Voz da Diaspora",
    sourceUrl: "https://vozdadiaspora.com/2026/02/embaixadora-de-angola-em-espanha-apresenta-cartas-figuradas-a-ministra-dos-negocios-estrangeiros-de-andorra/",
    date: "19 Fevereiro 2026",
    excerpt: "Balbina Malheiros Dias da Silva, Embaixadora Extraordinaria e Plenipotenciaria da Republica de Angola no Reino de Espanha e junto do Principado de Andorra, apresentou as Cartas Figuradas a Ministra dos Negocios Estrangeiros de Andorra, Imma Tor Faus.",
    image: "https://vozdadiaspora.com/wp-content/uploads/2026/02/cartas-andorra-991x564.jpeg"
  },
  {
    id: 2,
    title: "Diplomata realca afirmacao de Africa como espaco de crescimento real",
    source: "ANGOP",
    sourceUrl: "https://www.angop.ao/noticias/economia/diplomata-realca-afirmacao-de-africa-como-espaco-de-crescimento-real/",
    date: "Fevereiro 2026",
    excerpt: "A embaixadora de Angola em Espanha destacou que investir em Africa e hoje uma necessidade estrategica para quem pretende crescer e diversificar a economia.",
    image: "https://customer-assets.emergentagent.com/job_50e85d91-1f1d-48c5-a645-eaeea3c1f4b7/artifacts/mc6fn6ht_Angop.png"
  },
  {
    id: 3,
    title: "Pais assume papel de facilitador de investimentos no continente",
    source: "Jornal de Angola",
    sourceUrl: "https://www.jornaldeangola.ao/noticias/1/pol%C3%ADtica/658651/pa%C3%ADs-assume-papel-de-facilitador-de-investimentos-no-continente",
    date: "7 Fevereiro 2026",
    excerpt: "A embaixadora Balbina da Silva afirmou, em Barcelona, que Angola esta preparada para ser uma das principais portas de entrada do novo ciclo de desenvolvimento no continente africano.",
    image: "https://customer-assets.emergentagent.com/job_50e85d91-1f1d-48c5-a645-eaeea3c1f4b7/artifacts/qhd7yd4d_JA.png"
  },
  {
    id: 4,
    title: "Plaza de Toros de Las Ventas acogera por primera vez el Dia de Angola con motivo del 50 aniversario de su independencia",
    source: "El Trapezio",
    sourceUrl: "https://eltrapezio.eu/es/2025/10/25/plaza-de-toros-de-las-ventas-acogera-por-primera-vez-el-dia-de-angola-con-motivo-del-50o-aniversario-de-su-independencia/",
    date: "25 Octubre 2025",
    excerpt: "La Embajada de Angola en Espana ha anunciado la organizacion del Dia de Angola 2025, un evento que conmemorara el 50 Aniversario de la Independencia de Angola el 11 de noviembre en Las Ventas, Madrid.",
    image: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/4kxfa3mv_50%20Aniversario_Independencia.jpeg"
  },
  {
    id: 5,
    title: "La nueva embajadora de Angola, Balbina Malheiros Dias da Silva, presenta sus Copias de Estilo",
    source: "The Diplomat in Spain",
    sourceUrl: "https://thediplomatinspain.com/2025/05/21/la-nueva-embajadora-de-angola-balbina-malheiros-dias-da-silva-presenta-sus-copias-de-estilo/",
    date: "21 Mayo 2025",
    excerpt: "La nueva embajadora de Angola, Balbina Malheiros Dias da Silva, ha presentado las Copias de Estilo de sus Cartas Credenciales al director general de Protocolo en el Ministerio de Asuntos Exteriores de Espana.",
    image: "https://thediplomatinspain.com/wp-content/uploads/2025/05/embajadora-de-angola-750x375.jpg"
  }
];

export default function News() {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  useEffect(() => {
    api.get('/events?limit=6')
      .then(r => setEvents(r.data || []))
      .catch(console.error)
      .finally(() => setEventsLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t('news.title')}</h1>
          <p className="text-gray-500 text-sm md:text-base">{t('news.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* External News Headlines Section */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-5">
            <Newspaper size={20} className="text-[#CC0000]" />
            <h2 className="text-xl font-bold text-gray-900" data-testid="featured-news-title">{t('news_events.featured_news')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {EXTERNAL_NEWS.map(news => (
              <a
                key={news.id}
                href={news.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all hover:border-[#CC0000]/30"
                data-testid={`external-news-${news.id}`}
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={news.image} alt={news.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 bg-[#CC0000] text-white text-xs font-semibold rounded-md flex items-center gap-1">
                      <ExternalLink size={10} />
                      {news.source}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-xs text-gray-400 mb-2">{news.date}</p>
                  <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-2 group-hover:text-[#CC0000] transition-colors line-clamp-2">
                    {news.title}
                  </h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{news.excerpt}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-[#CC0000] font-medium">
                    {t('home.read_article')} <ExternalLink size={10} />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-8" />

        {/* Proximos Eventos Section */}
        <div className="flex items-center gap-2 mb-5">
          <Calendar size={20} className="text-[#CC0000]" />
          <h2 className="text-xl font-bold text-gray-900" data-testid="events-section-title">{t('home.events_title')}</h2>
        </div>

        {eventsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-28 bg-white rounded-xl animate-pulse" />)}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Calendar size={40} className="mx-auto mb-4 opacity-40" />
            <p>{t('events.no_events')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map(evt => (
              <div key={evt.id} className="flex gap-4 p-5 bg-white border border-gray-100 rounded-xl hover:border-red-100 hover:shadow-md transition-all" data-testid={`event-${evt.id}`}>
                <div className="flex-shrink-0 w-14 h-14 bg-[#CC0000] rounded-lg flex flex-col items-center justify-center text-white">
                  <span className="text-xs font-bold">{new Date(evt.start_date).toLocaleString('pt', {month:'short'}).toUpperCase()}</span>
                  <span className="text-lg font-bold leading-none">{new Date(evt.start_date).getDate()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-gray-900 line-clamp-1">{evt.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{evt.description}</p>
                  {evt.location && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                      <MapPin size={11} /> {evt.location}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
